import {
    selectFlightById,
    selectVideoColorFinderFlightById,
} from "features/entity-caching/flights/selectors"
import {
    selectSet,
    selectUploadsForSet,
    selectPendingUploadsForSet,
    deleteUpload,
    uploadFlight,
    stageFiles,
    setSplitButtonOpen,
    selectSplitButtonOpen,
    selectSelectedUpload,
    selectStatusCheckerOpen,
    setSelectedUpload,
    setStatusCheckerOpen,
    selectVideoColorFinderJobByFlightId,
    selectVideoColorFinderUploads,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
} from "./ColorFinderSlice"
import {
    mapExtensionsToMimeType,
    supportedFileUploadExtensions,
} from "features/job-management/uploader/constants"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { deactivatedFlightPathAction } from "features/entity-caching/flights/slice"
import {
    useColorFinderImageProcessing,
    useColorFinderVideoProcessing,
} from "./useColorFinderProcessing"
import { config } from "@nova/configuration/browser"
import { colorFinderWarningDefinitions } from "./ColorFinderWarnings"
import {
    AddOns,
    ImageProcessingState,
    SampleDataFlightKey,
    UploadComponentProps,
} from "@nova/constants"
import { getEmbeddedSrt } from "../processing/srt"
import { getTelemetryType } from "@nova/telemetry-parsing"

const fileFilter = async (files: File[]) => {
    const videoFile = files.find((file) => file.name.toLowerCase().endsWith(".mp4"))
    if (!videoFile) return files
    const srtFile = files.find((file) => file.name.toLowerCase().endsWith(".srt"))
    if (!srtFile) {
        const embeddedSrt = await getEmbeddedSrt(videoFile)
        if (embeddedSrt && getTelemetryType(embeddedSrt) !== undefined)
            files.push(new File([new Blob([embeddedSrt])], "embedded.srt"))
    }
    return files.sort(
        (a, b) =>
            (a.name.toLowerCase().endsWith(".mp4") ? 0 : 1) -
            (b.name.toLowerCase().endsWith(".mp4") ? 0 : 1)
    )
}
import {
    deleteVideoColorFinderFlight,
    updateVideoColorFinderFlight,
} from "features/pubsub/entityActions/flights/videoColorFinder"
import { deleteImageColorFinderFlight } from "features/pubsub/entityActions/flights/imageColorFinder"
import { selectImageColorFinderMockJob } from "../ImageColorFinder/ImageColorFinderSlice"
import {
    CreateUploadFlow,
    CreateUploadFlowProps,
    getGenericUploadFlowRenderComponent,
} from "panels/content/side-panel/upload-flow/UploadFlow"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { GenericAddFiles } from "panels/content/side-panel/upload-flow/GenericAddFiles"
import { VideoColorfinderPreviewFiles } from "./preview-files/VideoColorfinderPreviewFiles"
import { get } from "nodash"
import { ColorfinderCreateFromLayer } from "../plugin-actions/ColorfinderCreateFromLayer"
import { selectColorFinderUploadSize } from "features/entity-caching/images/orthos/selectors"
import { imageColorFinderProps } from "plugins/ColorFinder/ImageColorFinder/ImageColorFinderMultifileUpload"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { GenericPreviewFiles } from "panels/content/side-panel/upload-flow/preview-files/GenericPreviewFiles"

const sharedUploadDefinition = {
    useAppDispatch,
    useAppSelector,
    supportedFileTypes: supportedFileUploadExtensions.video,
    selectFlight: selectFlightById,
    selectSet,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectPendingUploadsForSet,
    deleteUpload,
    uploadFlight,
    stageFiles,
    deactivatedFlightPathAction,
    mapExtensionsToMimeType,
    warningData: colorFinderWarningDefinitions,
    selectFlightById,
    fileTypeSeparator: " + ",
    useDateFormat: () => config.use("organizationSettingDateFormat"),
    useTimeFormat: () => config.use("organizationSettingTimeFormat"),
    setSplitButtonOpen,
    selectSplitButtonOpen,
    selectSelectedUpload,
    selectStatusCheckerOpen,
    setSelectedUpload,
    setStatusCheckerOpen,
    setSelectedPlugin: activateSidePanelModal,
    processingMessage:
        "Do not close the browser tab or you will lose your data, processing will happen in the background as you navigate the app.",
    pluginImageUrl: "/images/plugins/layers/colorfinder.svg",
    pluginId: NovaPluginIds.Colorfinder,
}

const VideoUploadFlow = CreateUploadFlow({
    ...sharedUploadDefinition,
    selectUploadsForSet: selectVideoColorFinderUploads,
    RenderComponent: getGenericUploadFlowRenderComponent({
        AddFiles: GenericAddFiles,
        PreviewFiles: VideoColorfinderPreviewFiles,
    }),
    useProcessing: useColorFinderVideoProcessing,
    fileFilter,
    supportedFileUploadExtensions: supportedFileUploadExtensions.video,
    minUploads: 1,
    addOnType: AddOns.Colorfinder,
    selectJob: selectVideoColorFinderJobByFlightId,
    deleteFlight: deleteVideoColorFinderFlight,
    selectFlightById: selectVideoColorFinderFlightById,
    updateFlight: updateVideoColorFinderFlight,
    dropboxTitle: "Upload Video",
    maxUploads: 2,
    selectUploadSize: selectColorFinderUploadSize,
    sampleDataFlightKey: SampleDataFlightKey.vcf2,
} as unknown as CreateUploadFlowProps)

const ImageUploadFlow = CreateUploadFlow({
    ...sharedUploadDefinition,
    ...imageColorFinderProps,
    useProcessing: useColorFinderImageProcessing,
    minUploads: 1,
    addOnType: AddOns.Colorfinder,
    selectJob: selectImageColorFinderMockJob,
    deleteFlight: deleteImageColorFinderFlight,
} as CreateUploadFlowProps)

const VideoRenderComponent = getGenericUploadFlowRenderComponent({
    AddFiles: GenericAddFiles,
    PreviewFiles: VideoColorfinderPreviewFiles,
})

const ImageRenderComponent = getGenericUploadFlowRenderComponent({
    AddFiles: GenericAddFiles,
    PreviewFiles: (props) => (
        <GenericPreviewFiles
            {...props}
            uploadPresentationOptions={{
                completeStates: [ImageProcessingState.UploadDone, ImageProcessingState.IngestDone],
            }}
        />
    ),
})

export const ColorFinderMultifileUpload = (props: UploadComponentProps) => {
    const flight = useAppSelector(selectVideoColorFinderFlightById(props.id))
    const isCreatedFromVideo = get(flight, "isCreatedFromVideo")
    if (isCreatedFromVideo) {
        if (!flight) return null
        return <ColorfinderCreateFromLayer flight={flight} />
    }
    if (flight) {
        return (
            <VideoUploadFlow {...props}>
                <VideoRenderComponent />
            </VideoUploadFlow>
        )
    } else {
        return (
            <ImageUploadFlow {...props}>
                <ImageRenderComponent />
            </ImageUploadFlow>
        )
    }
}
